// Endpoints whose errors are expected by design and should never surface as toasts.
// - checkIdToken: session-probe, failure means "no session yet"
// - loginByEmail / loginWithOAuth: Auth page renders inline errors (e.g. user_not_found
//   below the email input) and shows its own toasts where appropriate.
// - logout: best-effort cleanup; a server-side 403/404 shouldn't block local sign-out
//   or surface during flows like waitlist sign-up where logout fires opportunistically.
// - getCourseReviews: блок отзывов на странице статистики сам показывает ошибку и «Повторить».
// - getVideo: фоновый поллинг статуса Kinescope. До привязки видео к сохранённому
//   блоку бек правомерно отдаёт 404 video_not_found (доступ гейтится владельцем-блоком),
//   а при замене ролика новый videoId ещё не сохранён — поллинг тостить не должен,
//   StatusBadge просто ничего не показывает и подхватит статус после сохранения.
// - getOAuthPending / approveOAuth / denyOAuth / revokeConnection: MCP OAuth-consent
//   и connections-экраны рендерят собственный inline error-UX (сообщения/тосты внутри
//   компонента) — глобальный тост поверх был бы избыточным/противоречивым.
//   getConnections НЕ входит в этот список — на списке подключений глобальный тост
//   остаётся единственным сигналом сетевой ошибки (spec §6).
// - startCourseGeneration / startImageGeneration / startBlockEdit /
//   startTranslation / getQuote / getStudioJob / getCreditsBalance: студийные
//   AI-прогоны разбирают отказы сами (parseStartError) и показывают точное
//   состояние по месту — «функция не настроена на окружении», «курс занят
//   прогоном #N», «не хватает N кредитов». Глобальный тост поверх сказал бы
//   «Server error, please retry» и «Conflict», то есть соврал бы.
// - getMyPayments: история платежей объясняет отказ по месту, как и история
//   движений.
// - getCreditsHistory: окно кредитов объясняет отказ по месту и даёт повтор;
//   глобальный тост поверх задваивал бы сообщение.
// - getCreditPackages / buyCredits / getPaymentStatus: диалог пополнения
//   разбирает свои отказы сам (`buyFailureText`) и показывает их по месту —
//   «этот пакет больше не доступен», «для оплаты картой нужен email». Опрос
//   статуса тем более молчит: его 404 означает не поломку, а то, что ручка не
//   покрывает кредитные транзакции, и диалог переходит в ручное обновление.
// - cancelPayment: отмена брошенной оплаты — best-effort, автор уже ушёл к витрине,
//   и тост об отказе снять транзакцию его только запутал бы.
// - answerGate: ответ на вопрос прогона разбирает свои отказы сам
//   (`useAnswerGate` → `GateCard`). Глобальный тост поверх `409 gate_expired`
//   показал бы сырой JSON («{"retry":false,"code":"gate_expired",...}») —
//   `asMessage` делает `JSON.stringify` объектному `detail`, а этот код
//   означает не поломку клика, а то, что следующий тик поллера уже принёс
//   `state: "expired"`, и карточка скажет об этом сама.
// - cancelJob / deleteTranslation / changeSourceLang / declareTranslation: языковые действия курса
//   разбирают отказы сами (`courseLanguageFailureText`) — глобальный тост
//   показал бы «Conflict» или JSON объектного `detail`.
// - getCourseModeration / setCourseVisibility: состояние проверки курса опрашивается
//   каждые ~3 с, тост на каждый неудачный опрос был бы спамом, а отказы смены
//   видимости (валидатор, `429 review_rate_limited`, чужой прогон) разбирает по
//   месту `parseModerationError`. `409 review_in_progress` middleware при этом
//   всё равно инвалидирует тег `Moderation`.
// - getArticleModeration / setArticleVisibility / submitArticleUpdate / discardArticleDraft:
//   то же для статьи; по `409 review_in_progress` перечитывается её тег `ArticleModeration`
//   (какая ручка — статьи, решает `moderation/lib/reviewInProgress.ts`). `translateArticle`
//   здесь НЕТ намеренно: тост «Статья на проверке — перевод недоступен» — единственный
//   сигнал отказа, `useTranslation` своего не показывает.
// - getIdProfile / changeAvatar / updateProfileUsername / changeEmail / verifyEmail:
//   профиль в ID-сервисе (ник, аватарка, почта). Чтение — фоновая проба: отказ значит
//   «ID-сессии для правки нет» (например, на localhost без общей куки), и экран просто
//   прячет кнопки правки. Правки разбирают отказы в самих диалогах, глобальный тост
//   задвоил бы сообщение.
// - updateInstruction / deleteInstruction: редактор и списки статей разбирают отказ сами
//   (`instructions/lib/errorMessage`, `409 review_in_progress` — `useEditorActions`), глобальный
//   тост поверх давал второй, другими словами. Тег `ArticleModeration` по `review_in_progress`
//   middleware перечитывает и для молчащей ручки.
// - getAiJob: опрос прогона перевода статьи; отказ опроса тостит сам `useTranslation`
//   («Не удалось получить статус перевода») и останавливает опрос.
export const SILENT_ENDPOINTS = new Set([
  'getIdProfile',
  'changeAvatar',
  'updateProfileUsername',
  'changeEmail',
  'verifyEmail',
  'checkIdToken',
  'loginByEmail',
  'loginWithOAuth',
  'logout',
  'getVideo',
  // Обложки ролика: та же причина, что у getVideo — до сохранения ролика в блоке
  // бек отдаёт 404; миниатюра без обложки просто показывает значок «плей».
  'getPosters',
  'getCourseReviews',
  'getOAuthPending',
  'approveOAuth',
  'denyOAuth',
  'revokeConnection',
  'startCourseGeneration',
  'startImageGeneration',
  'startBlockEdit',
  'startTranslation',
  'getQuote',
  'getStudioJob',
  'getCreditsBalance',
  'getCreditsHistory',
  'getCreditsUsage',
  'getCreditPackages',
  'buyCredits',
  'getPaymentStatus',
  'cancelPayment',
  'getMyPayments',
  'answerGate',
  // Публикация языка перевода: её два бизнес-отказа (`translation_storage_limit`,
  // `unsupported_language`) разбирает тот же словарь, что и отказы старта, и
  // показывает строкой прямо у переключателя (`TranslationLangRow`). Тост
  // поверх задваивал бы сообщение, причём сырым английским текстом бэка.
  'setTranslationPublished',
  'cancelJob',
  'deleteTranslation',
  'changeSourceLang',
  'declareTranslation',
  'getCourseModeration',
  'setCourseVisibility',
  'getUpdateImpact',
  'submitCourseUpdate',
  'discardCourseDraft',
  'getArticleModeration',
  'setArticleVisibility',
  'submitArticleUpdate',
  'discardArticleDraft',
  'updateInstruction',
  'deleteInstruction',
  'getAiJob',
])
